import { Brand, Module, Store } from "@apis/index";
import Header from "@commons/header";
import ModuleDropdown from "@commons/modules/dropdown";
import Button from "@components/button";
import Column from "@components/column";
import Fields from "@components/fields";
import DropDown from "@components/fields/dropdown";
import Row from "@components/row";
import Wrapper from "@components/wrapper";
import ButtonWrapper from "@components/wrapper/button-wrapper";
import { RootState } from "@store/index";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

const BrandForm = () => {
  const { data: user } = useSelector((state: RootState) => state.user);
  const [searchTerm] = useSearchParams();
  const module = searchTerm?.get("module") || "Food";
  const { state } = useLocation();
  const navigate = useNavigate();
  const { data: modules } = useSelector((state: RootState) => state.modules);
  const { items: stores } = useSelector((state: RootState) => state.stores);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const fetchModule = async () => {
    await Module.list({ state: "modules" });
  };

  const fetchStores = async (moduleId?: any) => {
    await Store.list({
      state: "stores",
      params: { limit: 3000, moduleId: moduleId || undefined },
    });
  };

  const [initialValues, setInitialValues] = useState({
    module: "",
    name: "",
    image: null,
    coverImage: null,
    linkType: "link", // ✅ ADDED: Link type selector
    bannerLinks: [] as string[],
    storeIds: [] as any[],
  });

  useEffect(() => {
    if (state?.id) {
      // ✅ IMPROVED: Determine link type based on existing data
      let linkType = "link";
      if (state?.banners?.some((banner: any) => banner.storeId)) {
        linkType = "store";
      }

      setInitialValues({
        module: state.module.id,
        name: state.name,
        image: state.image,
        coverImage: state.banners,
        linkType: linkType, // ✅ SET link type
        bannerLinks:
          state?.banners?.map((banner: any) => banner.link || "") || [],
        storeIds:
          state?.banners?.map((banner: any) => banner.storeId || "") || [],
      });
      if (state.module.id) {
        fetchStores(state.module.id);
      }
    } else {
      setInitialValues({
        module: "",
        name: "",
        image: null,
        coverImage: null,
        linkType: "link", // ✅ DEFAULT to link
        bannerLinks: [] as string[],
        storeIds: [] as any[],
      });
    }
  }, [state?.id]);

  useEffect(() => {
    fetchModule();
    fetchStores(initialValues.module);
  }, []);

  const createFormData = (values: typeof initialValues) => {
    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("moduleId", values.module);

    if (values.image?.rawFile) formData.append("image", values.image.rawFile);

    values.coverImage.forEach((banner, idx) => {
      if (!!state?.id || user?.role != "store") {
        if (banner?.id)
          formData.append(`banners[${idx}][id]`, JSON.stringify(banner.id));
      }
      formData.append(`banners[${idx}][image]`, banner.rawFile);
      if (state?.banners?.[idx]?.image) {
        formData.append(`banners[${idx}][oldImage]`, state.banners[idx].image);
      }
      if (values.linkType === "store") {
        formData.append(`banners[${idx}][storeId]`, values.storeIds[idx] || "");
      }
      if (values.linkType === "link") {
        formData.append(`banners[${idx}][link]`, values.bannerLinks[idx] || "");
      }
    });

    // if (values.coverImage) {
    //   const images = Array.isArray(values.coverImage)
    //     ? values.coverImage
    //     : [values.coverImage];

    //   console.log("Cover images:", images);

    //   // Append images
    //   images.forEach((coverImage) => {
    //     if (coverImage.rawFile) {
    //       formData.append("banners", coverImage.rawFile);
    //     }
    //   });

    //   // ✅ IMPROVED: Handle data based on link type
    //   if (values.linkType === "store") {
    //     // For store type, append storeIds
    //     const storeIdsArray = Array.isArray(values.storeIds)
    //       ? values.storeIds
    //       : values.storeIds
    //         ? [values.storeIds]
    //         : [];

    //     console.log("Store IDs:", storeIdsArray);

    //     storeIdsArray.forEach((storeId, index) => {
    //       formData.append("storeIds", storeId || "");
    //     });
    //   } else {
    //     // For link type, append bannerLinks
    //     const bannerLinksArray = Array.isArray(values.bannerLinks)
    //       ? values.bannerLinks
    //       : values.bannerLinks
    //         ? [values.bannerLinks]
    //         : [];

    //     console.log("Banner Links:", bannerLinksArray);

    //     bannerLinksArray.forEach((link, index) => {
    //       formData.append("bannerLinks", link || "");
    //     });
    //   }
    // }

    return formData;
  };

  const handleSubmit = async (
    values: typeof initialValues,
    formikHelpers: any,
  ) => {
    const { resetForm, setSubmitting } = formikHelpers;
    setSubmitAttempted(true); // ✅ Mark that submit was attempted

    // ✅ Manual validation before submitting
    const errors = validateForm(values);

    if (Object.keys(errors).length > 0) {
      // Show toast for each error
      Object.values(errors).forEach((error: any) => {
        toast.error(error);
      });
      setSubmitting(false);
      return; // Stop submission if there are errors
    }

    const formData = createFormData(values);

    try {
      // ✅ ADDED: toast.promise for better user experience
      const promise = state?.id
        ? Brand.update({
            state: "brands",
            data: formData,
            id: state?.id,
          })
        : Brand.create({ state: "brands", data: formData });

      await toast.promise(promise, {
        pending: state?.id ? "Updating brand..." : "Creating brand...",
        success: state?.id
          ? "Brand updated successfully!"
          : "Brand created successfully!",
        error: "Something went wrong while saving the brand.",
      });

      const res = await promise;
      if (!!state?.id) {
        navigate(-1);
      }
      navigate(`/admin/product-management/brands?module=${res.module.name}`);

      resetForm({
        values: {
          module: "",
          name: "",
          image: null,
          coverImage: null,
          linkType: "link",
          bannerLinks: [],
          storeIds: [],
        },
      });
      setSubmitAttempted(false); // ✅ Reset submit attempt on success
    } catch (error) {
      // Error is already handled by toast.promise, but we can add additional handling if needed
      console.error("Form submission error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ Separate validation function
  const validateForm = (values: typeof initialValues) => {
    const errors: any = {};

    // ✅ VALIDATION: At least one cover image is required
    const imageCount = values.coverImage
      ? Array.isArray(values.coverImage)
        ? values.coverImage.length
        : 1
      : 0;

    if (imageCount < 1) {
      errors.coverImage = "At least one cover image is required";
    }

    if (imageCount > 0) {
      if (values.linkType === "store") {
        const storeIdsCount = Array.isArray(values.storeIds)
          ? values.storeIds.length
          : values.storeIds
            ? 1
            : 0;

        if (storeIdsCount > 1 && storeIdsCount !== imageCount) {
          errors.storeIds = `Number of stores (${storeIdsCount}) must match number of images (${imageCount})`;
        }
      } else {
        const bannerLinksCount = Array.isArray(values.bannerLinks)
          ? values.bannerLinks.length
          : values.bannerLinks
            ? 1
            : 0;

        if (bannerLinksCount > 1 && bannerLinksCount !== imageCount) {
          errors.bannerLinks = `Number of links (${bannerLinksCount}) must match number of images (${imageCount})`;
        }
      }
    }

    return errors;
  };

  return (
    <>
      <Header title="Brand Setup" />
      <Wrapper stylewrapper={`border-radius:12px`}>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize={true}
          validate={submitAttempted ? validateForm : undefined} // ✅ Only validate after submit attempt
        >
          {({
            values,
            setFieldValue,
            resetForm,
            isSubmitting,
            errors,
          }: any) => (
            <Form>
              <Row>
                <div style={{ flex: 3, width: "100%" }}>
                  <Column>
                    <ModuleDropdown
                      styleInput="width:97.6%"
                      name="module"
                      onChange={(val) => {
                        setFieldValue("module", val);
                        setFieldValue("storeIds", []);
                        fetchStores(val);
                      }}
                    />

                    <Fields.Input
                      label="Brand Name"
                      name="name"
                      placeholder="Enter Brand Name"
                    />
                  </Column>
                </div>
                <br />
                <div style={{ flex: 2, width: "100%" }}>
                  <Fields.Upload
                    name="image"
                    height={200}
                    useRawFile={true}
                    label="Icon Image"
                  />
                </div>
              </Row>

              <br />

              {/* ✅ ADDED: Link Type Selector */}
              <Row>
                <DropDown
                  name="linkType"
                  label="Cover Image Link Type"
                  options={[
                    { title: "Redirect Link", value: "link" },
                    { title: "Store/Vendor", value: "store" },
                  ]}
                  titleAsKey="title"
                  valueAsKey="value"
                  styleInput="width:97%"
                  styleOptionsWrapper="width:100%"
                  placeholder="Select Link Type"
                  onChange={(value) => {
                    setFieldValue("linkType", value);
                    // Clear the other field when switching types
                    if (value === "link") {
                      setFieldValue("storeIds", []);
                    } else {
                      setFieldValue("bannerLinks", []);
                    }
                  }}
                />
                {values.linkType === "store" ? (
                  <div style={{ width: "100%" }}>
                    <DropDown
                      name="storeIds"
                      label="Store"
                      options={stores}
                      titleAsKey="storeName"
                      valueAsKey="id"
                      styleInput="width:97%"
                      styleOptionsWrapper="width:100%"
                      placeholder="Select Store"
                      isMultiple={true}
                      hasSearch
                    />
                    {submitAttempted && errors.storeIds && (
                      <div
                        style={{
                          color: "red",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {errors.storeIds}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ width: "100%" }}>
                    <Fields.TagInput
                      allowSame={true}
                      name="bannerLinks"
                      label="Cover Image Links"
                      placeholder="Enter cover image redirect links"
                      icon={false}
                    />
                    {submitAttempted && errors.bannerLinks && (
                      <div
                        style={{
                          color: "red",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {errors.bannerLinks}
                      </div>
                    )}
                  </div>
                )}
              </Row>

              <br />

              <div>
                <Fields.Upload
                  name="coverImage"
                  height={400}
                  size={90}
                  useRawFile={true}
                  maxFiles={10}
                  multiple={true}
                  label="Cover Image"
                  mode={!!state || state?.id ? "update" : "create"}
                  onRemoveImage={async (val) => {
                    await Brand.removeBanner({ state: "brands", id: val.id });
                  }}
                />
                {submitAttempted && errors.coverImage && (
                  <div
                    style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
                  >
                    {errors.coverImage}
                  </div>
                )}
              </div>

              <br />

              {/* ✅ VALIDATION SUMMARY - Only show after submit attempt */}
              {submitAttempted && Object.keys(errors).length > 0 && (
                <div
                  style={{
                    backgroundColor: "#fff3f3",
                    border: "1px solid #ffcdd2",
                    borderRadius: "8px",
                    padding: "12px",
                    marginBottom: "16px",
                  }}
                >
                  <strong style={{ color: "#d32f2f" }}>
                    Please fix the following errors:
                  </strong>
                  <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                    {errors.coverImage && <li>{errors.coverImage}</li>}
                    {errors.storeIds && <li>{errors.storeIds}</li>}
                    {errors.bannerLinks && <li>{errors.bannerLinks}</li>}
                  </ul>
                </div>
              )}

              <ButtonWrapper>
                <Button
                  title="Reset"
                  type="button"
                  variant="reset"
                  onClick={() => {
                    resetForm({
                      values: {
                        module: "",
                        name: "",
                        image: null,
                        coverImage: null,
                        linkType: "link",
                        bannerLinks: [],
                        storeIds: [],
                      },
                    });
                    setSubmitAttempted(false); // ✅ Reset submit attempt on reset
                  }}
                  disabled={isSubmitting}
                />
                <Button
                  title={state?.id ? "Update" : "Save"}
                  type="submit"
                  disabled={isSubmitting}
                />
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </Wrapper>
    </>
  );
};

export default BrandForm;
